/**
 * Purchase options selector (one-time vs. subscribe).
 *
 * Pairs with blocks/subscription-options.liquid. Three jobs:
 *
 *  1. Keep the `selling_plan` input enabled only while "subscribe" is chosen.
 *     Disabled inputs are excluded from `new FormData(form)`, which is what
 *     product-form.js submits — so a one-time purchase posts no selling plan,
 *     and a subscription posts exactly one.
 *
 *  2. Swap the displayed subscription price when the delivery frequency changes.
 *
 *  3. Re-render itself on variant change. Horizon does NOT re-render the whole
 *     section when a variant is selected — variant-picker.js morphs only the
 *     picker, and every other component subscribes to `variant:update` and
 *     refreshes itself from the freshly rendered HTML in the event detail.
 *     Without this, prices here would stay pinned to the first variant.
 *
 * Deliberately has no theme imports: the block can be placed on any product
 * template and needs to work without an importmap entry. The one constant it
 * would otherwise import is ThemeEvents.variantUpdate, inlined below.
 */

/** @see assets/events.js — ThemeEvents.variantUpdate */
const VARIANT_UPDATE = 'variant:update';

/** Emitted whenever the displayed prices change. @see assets/direct-checkout-button.js */
const PRICING_CHANGE = 'vt:pricing-change';

/**
 * Remembers each product's choice so it survives the re-render on variant
 * change. Keyed by product, so it persists as the shopper moves between
 * variants of the same product.
 *
 * @type {Map<string, { subscribe: boolean, planId: string | null }>}
 */
const rememberedChoice = new Map();

class SubscriptionOptions extends HTMLElement {
  /** @type {Element | null} */
  #section = null;

  // Bound once as fields so add/removeEventListener see the same reference.
  // (A private method can't be reassigned, so binding in place is not an option.)
  #handleChange = (event) => this.#onChange(event);
  #handleVariantUpdate = (event) => this.#onVariantUpdate(event);

  connectedCallback() {
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(VARIANT_UPDATE, this.#handleVariantUpdate);
    this.addEventListener('change', this.#handleChange);

    this.#init();
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(VARIANT_UPDATE, this.#handleVariantUpdate);
    this.removeEventListener('change', this.#handleChange);
  }

  /**
   * Re-reads the DOM and re-applies state. Runs on connect and again after
   * every variant swap, since the swap replaces the elements the refs point at.
   */
  #init() {
    this.onetimeRadio = this.querySelector('[data-role="onetime"]');
    this.subscribeRadio = this.querySelector('[data-role="subscribe"]');
    this.planInput = this.querySelector('[data-role="selling-plan"]');
    this.priceEl = this.querySelector('[data-role="subscribe-price"]');
    this.wasEl = this.querySelector('[data-role="subscribe-was"]');

    if (!this.onetimeRadio || !this.subscribeRadio || !this.planInput) return;

    this.#ensureFormAssociation();
    this.#restoreChoice();
    this.#sync();
  }

  get #productKey() {
    return this.dataset.productId || 'default';
  }

  /**
   * The prices currently on screen for whichever option is selected. Public so
   * other blocks (the direct checkout button) can label themselves from one
   * source of truth rather than scraping this block's markup.
   *
   * @returns {{ price: string, compareAt: string, subscribing: boolean }}
   */
  get currentPricing() {
    const subscribing = Boolean(this.subscribeRadio?.checked);

    const priceEl = subscribing ? this.priceEl : this.querySelector('[data-role="onetime-price"]');
    const wasEl = subscribing ? this.wasEl : this.querySelector('[data-role="onetime-was"]');

    return {
      price: priceEl?.textContent?.trim() ?? '',
      compareAt: wasEl && !wasEl.hidden ? wasEl.textContent?.trim() ?? '' : '',
      subscribing,
    };
  }

  /**
   * Replaces the card markup with the version Liquid rendered for the newly
   * selected variant — prices, discount badge and the set of delivery
   * frequencies can all differ per variant.
   *
   * @param {Event & { detail?: any }} event
   */
  #onVariantUpdate(event) {
    const data = event.detail?.data;
    if (!data?.html) return;

    // Combined listings can swap the product entirely; otherwise ignore events
    // belonging to a different product form on the same page.
    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (data.productId && data.productId !== this.dataset.productId) {
      return;
    }

    const blockId = this.dataset.blockId;

    // Match by block id when available, but fall back to any rendered block.
    // If this file and the Liquid ever fall out of sync — a rolled-back block
    // file predating data-block-id, for instance — an id lookup returns null,
    // which is indistinguishable from "this variant has no plans". Without the
    // fallback the block hides itself on every variant change and takes the
    // subscribe option down with it.
    const fresh =
      (blockId && data.html.querySelector(`subscription-options[data-block-id="${blockId}"]`)) ||
      data.html.querySelector('subscription-options');

    // Genuinely no block in the newly rendered HTML: this variant has no
    // selling plans. Hide, and make sure a stale plan from the previous
    // variant can't be submitted.
    if (!fresh) {
      if (this.planInput) this.planInput.disabled = true;
      this.hidden = true;
      return;
    }

    const currentSet = this.querySelector('.vt-purchase__set');
    const newSet = fresh.querySelector('.vt-purchase__set');
    if (currentSet && newSet) currentSet.replaceWith(newSet);

    this.hidden = false;
    this.#init();
  }

  /**
   * The Liquid `form` attribute assumes buy-buttons.liquid's form id. If that
   * assumption fails — a custom template, a renamed form — `planInput.form` is
   * null and the selling plan would silently never reach /cart/add. Re-point it
   * at the real product form instead of failing quietly.
   */
  #ensureFormAssociation() {
    if (this.planInput.form) return;

    const productForm =
      this.closest('product-form-component')?.querySelector('form') ??
      document.querySelector('product-form-component form');

    if (!productForm) {
      console.warn('[subscription-options] No product form found; selling plan will not be submitted.');
      return;
    }

    // getAttribute, not `.id`: a form's named controls shadow its own
    // properties, and the product form contains <input name="id">, so
    // `productForm.id` returns that input element rather than the form's id.
    let formId = productForm.getAttribute('id');

    if (!formId) {
      formId = `vt-product-form-${Math.random().toString(36).slice(2, 9)}`;
      productForm.setAttribute('id', formId);
    }

    this.planInput.setAttribute('form', formId);
  }

  /**
   * Re-applies the shopper's previous choice after a re-render. Falls back to
   * whatever Liquid marked as checked on first load.
   */
  #restoreChoice() {
    const remembered = rememberedChoice.get(this.#productKey);
    if (!remembered) return;

    this.subscribeRadio.checked = remembered.subscribe;
    this.onetimeRadio.checked = !remembered.subscribe;

    // The available plans can differ between variants, so only restore a plan
    // that still exists on this variant.
    if (remembered.planId && this.planInput instanceof HTMLSelectElement) {
      const stillOffered = Array.from(this.planInput.options).some((option) => option.value === remembered.planId);
      if (stillOffered) this.planInput.value = remembered.planId;
    }
  }

  #onChange(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Ignore changes bubbling up from unrelated inputs (e.g. the quantity
    // selector, which shares the product form with this block).
    const role = event.target.dataset.role;
    if (role !== 'onetime' && role !== 'subscribe' && role !== 'selling-plan') return;

    this.#sync();
  }

  #sync() {
    const subscribing = this.subscribeRadio.checked;

    // The single source of truth for what gets posted to /cart/add.
    this.planInput.disabled = !subscribing;

    this.#updatePrice();
    this.#updateSelectedClasses(subscribing);

    rememberedChoice.set(this.#productKey, {
      subscribe: subscribing,
      planId: this.planInput.value || null,
    });

    // Broadcast after every state change — connect, option toggle, frequency
    // change and variant swap all funnel through here, so a listener that
    // handles this event alone can never fall out of step with the cards.
    this.dispatchEvent(
      new CustomEvent(PRICING_CHANGE, { bubbles: true, detail: this.currentPricing })
    );
  }

  #updatePrice() {
    if (!(this.planInput instanceof HTMLSelectElement) || !this.priceEl) return;

    const selected = this.planInput.options[this.planInput.selectedIndex];
    if (!selected) return;

    const price = selected.dataset.price;
    const compare = selected.dataset.compare;

    if (price) this.priceEl.textContent = price;

    if (this.wasEl) {
      this.wasEl.textContent = compare || '';
      this.wasEl.hidden = !compare;
    }
  }

  /**
   * `:has()` handles this in every browser the theme targets; the class is a
   * belt-and-braces fallback so the selected card is never ambiguous.
   */
  #updateSelectedClasses(subscribing) {
    const cards = this.querySelectorAll('.vt-purchase__card');

    for (const card of cards) {
      const isSubscribeCard = card.dataset.option === 'subscribe';
      card.classList.toggle('is-selected', isSubscribeCard === subscribing);
    }
  }
}

if (!customElements.get('subscription-options')) {
  customElements.define('subscription-options', SubscriptionOptions);
}
